import { FxRateUnavailable } from "@trader/shared/Errors"
import { type Candle, type Instrument, type Timeframe, VENUES } from "@trader/shared/MarketData"
import type { IChartApi, ISeriesApi, SeriesType } from "lightweight-charts"
import { ApiError, call, errorTag } from "../../api.ts"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import { DEEP_HISTORY_SECONDS } from "../chart/chartWindows.ts"
import { chartContextFor } from "../indicators/chartContext.ts"
import type { IndicatorModules } from "../indicators/indicatorModules.ts"
import type { StrategyInstance } from "../workspace/workspaceStore.ts"
import {
  accountCurrencyOf,
  type CurrencyProblem,
  fetchProblem,
  formatDay,
  fxRange,
  gapProblem,
  localDate,
  type RunFx,
  unavailableProblem
} from "./accountCurrency.ts"
import { draftKeyOf } from "../strategy-editor/draftDocument.ts"
import { fxSeries } from "./fxRates.ts"
import type { StrategyResult } from "./protocol.ts"
import { barsInRange } from "./backtestRange.ts"
import { StrategyLayer } from "./StrategyLayer.ts"
import { strategyResults } from "./strategyResults.ts"
import { runPaneStrategy } from "./strategyRuns.ts"
import { userDrafts } from "./userDrafts.ts"

/** A change runs the backtest at most this long after it, and at most once per this interval (bar replay steps fast). */
const RUN_THROTTLE_MS = 300

interface Target {
  readonly strategy: StrategyInstance
  readonly instrument: Instrument
  readonly timeframe: Timeframe
}

/**
 * The strategy of one chart pane: backtests it in the worker whenever its
 * settings or the chart's bars change (throttled, one run at a time: a change
 * during a run reruns when it ends, so a bar replay's steps still show reports
 * while it plays), publishes the report for the
 * Strategy Tester, and draws its trades and plots on the chart. Plain
 * TypeScript; ChartPane owns its lifetime.
 *
 * Deep backtest prepends the stored history older than the chart's bars,
 * fetched once per series, so live tail updates still rerun cheaply.
 */
export class PaneStrategy {
  private readonly layer: StrategyLayer
  private target: Target | null = null
  private bars: ReadonlyArray<Candle> = []
  private timer: ReturnType<typeof setTimeout> | undefined
  /** A run is in flight; `dirty` asks for another when it ends. */
  private running = false
  private dirty = false
  private history: { readonly key: string; readonly bars: Promise<ReadonlyArray<Candle>> } | null = null
  /** The latest report and the bars it ran on, as drawn on the chart. */
  private shown: { readonly result: StrategyResult; readonly bars: ReadonlyArray<Candle> } | null = null
  private disposed = false
  /** Increases with every run: a run that awaited history checks it's still the latest. */
  private runSeq = 0
  private readonly pane: number
  private readonly chart: IChartApi
  private readonly stopDrafts: () => void

  constructor(pane: number, chart: IChartApi, mainSeries: ISeriesApi<SeriesType>) {
    this.pane = pane
    this.chart = chart
    this.layer = new StrategyLayer(chart, mainSeries)
    strategyResults.setFocusHandler(pane, (time) => this.focus(time))
    // A draft on this pane changed in the editor ("Backtest"): run it again.
    this.stopDrafts = userDrafts.subscribe((key) => {
      if (this.target !== null && draftKeyOf(this.target.strategy.id) === key) {
        strategyResults.update(this.pane, { running: true })
        this.schedule()
      }
    })
  }

  /** The indicator code, once loaded: the strategy's own plots can then be drawn. */
  setModules(modules: IndicatorModules | null): void {
    if (this.disposed || modules === null) return
    this.layer.setModules(modules)
    this.redraw()
  }

  /** The pane's strategy (null: none), with the chart's symbol and timeframe. */
  setStrategy(strategy: StrategyInstance | null, instrument: Instrument, timeframe: Timeframe): void {
    if (this.disposed) return
    const previous = this.target
    this.target = strategy === null ? null : { strategy, instrument, timeframe }
    if (strategy === null) {
      clearTimeout(this.timer)
      this.timer = undefined
      this.shown = null
      this.layer.clear()
      strategyResults.clear(this.pane)
      return
    }
    // Hiding doesn't change the backtest, only what the chart shows.
    if (previous !== null && runKey(previous) === runKey(this.target!)) {
      this.redraw()
      return
    }
    // Another series or another strategy: the old report and trades don't belong
    // to it (and its settings dialog needs this strategy's inputs).
    const fresh = previous === null ||
      previous.instrument.id !== instrument.id ||
      previous.timeframe !== timeframe ||
      previous.strategy.id !== strategy.id
    if (fresh) {
      this.shown = null
      this.layer.clear()
      strategyResults.update(this.pane, {
        running: true,
        error: null,
        currencyProblem: null,
        result: null,
        barTimes: [],
        // Same strategy on another series: its settings info still applies.
        ...(previous?.strategy.id === strategy.id ? {} : { strategyInfo: null })
      })
    } else {
      strategyResults.update(this.pane, { running: true, error: null, currencyProblem: null })
    }
    this.schedule()
  }

  setBars(bars: ReadonlyArray<Candle>): void {
    if (this.disposed) return
    this.bars = bars
    if (this.target !== null) this.schedule()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.runSeq++
    clearTimeout(this.timer)
    this.timer = undefined
    this.stopDrafts()
    strategyResults.setFocusHandler(this.pane, null)
    strategyResults.clear(this.pane)
    this.layer.dispose()
  }

  private schedule(): void {
    if (this.timer !== undefined) return
    this.timer = setTimeout(() => {
      this.timer = undefined
      this.start()
    }, RUN_THROTTLE_MS)
  }

  private start(): void {
    if (this.disposed) return
    if (this.running) {
      this.dirty = true
      return
    }
    this.running = true
    void this.run().finally(() => {
      this.running = false
      if (this.dirty) {
        this.dirty = false
        this.schedule()
      }
    })
  }

  private async run(): Promise<void> {
    const target = this.target
    const chartBars = this.bars
    const seq = ++this.runSeq
    if (target === null) return
    // Taken now: a draft's revision moves on when the editor publishes a newer one.
    const key = runKey(target)
    // No bars yet (loading) or none at all: nothing to run until the feed delivers some.
    if (chartBars.length === 0) {
      strategyResults.update(this.pane, { running: false })
      return
    }
    strategyResults.update(this.pane, { running: true })
    let bars = chartBars
    if (target.strategy.deep) {
      try {
        const older = await this.olderHistory(target, chartBars[0]!.time)
        bars = [...older.filter((bar) => bar.time < chartBars[0]!.time), ...chartBars]
      } catch {
        // Stored history unavailable: backtest the chart's bars.
      }
      // A newer run started while the history loaded: it runs instead, so this one
      // never reaches the worker (where it would queue behind, and outlive, the newer one).
      if (!this.isLatest(seq, key)) return
    }
    // The Backtesting range (or a chosen start, "Start at"): only the bars inside it.
    const { from, to } = target.strategy
    bars = barsInRange(bars, { from, to })
    const timeZone = VENUES[target.instrument.venue].timezone
    if (bars.length === 0) {
      const days = [
        from === undefined ? null : `from ${formatDay(localDate(from, DISPLAY_TIME_ZONE))}`,
        to === undefined ? null : `to ${formatDay(localDate(to - 1, DISPLAY_TIME_ZONE))}`
      ].filter((part) => part !== null).join(" ")
      strategyResults.update(this.pane, {
        running: false,
        error: `there are no bars ${days}. Change the Backtesting range (Properties), clear it (✕ next to "From" / "To"), ` +
          `or tick Deep backtest for older history.`,
        currencyProblem: null
      })
      this.shown = null
      this.layer.clear()
      return
    }
    const barTimes = bars.map((bar) => bar.time)

    // Another account currency than the symbol's: fetch the pair's rates first (cached).
    const symbolCurrency = target.instrument.currency
    const currency = accountCurrencyOf(target.strategy.properties, symbolCurrency)
    let fx: RunFx | undefined
    if (currency !== symbolCurrency) {
      const range = fxRange(barTimes, timeZone)!
      try {
        const series = await fxSeries.get(symbolCurrency, currency, range.start, range.end)
        fx = { currency, source: series.source, derivedVia: series.derivedVia, dates: series.dates, rates: series.rates }
      } catch (error) {
        if (!this.isLatest(seq, key)) return
        this.fail(
          error instanceof ApiError && error.detail instanceof FxRateUnavailable
            ? unavailableProblem(error.detail, barTimes, timeZone)
            : fetchProblem(symbolCurrency, currency, errorTag(error))
        )
        return
      }
      if (!this.isLatest(seq, key)) return
    }

    const reply = await runPaneStrategy(this.pane, {
      strategyId: target.strategy.id,
      bars: bars.map(({ close, high, low, open, time, volume }) => ({ time, open, high, low, close, volume })),
      inputs: target.strategy.inputs,
      properties: target.strategy.properties,
      chart: chartContextFor(target.instrument, target.timeframe),
      ...(fx ? { fx } : {})
    })
    // Replaced, or settings changed meanwhile: a newer run is on its way.
    if (reply.kind === "superseded" || !this.isCurrent(key)) return
    if (reply.kind === "error") {
      if (reply.fxGap !== undefined) this.fail(gapProblem(reply.fxGap, symbolCurrency, currency))
      else {
        strategyResults.update(this.pane, { running: false, error: reply.message, currencyProblem: null })
        this.shown = null
        this.layer.clear()
      }
      return
    }
    strategyResults.update(this.pane, {
      running: false,
      error: null,
      currencyProblem: null,
      result: reply.result,
      barTimes,
      strategyInfo: { strategyId: target.strategy.id, result: reply.result }
    })
    this.shown = { result: reply.result, bars }
    this.redraw()
  }

  /** The run can't be converted to the account currency: say why, and drop what it drew. */
  private fail(problem: CurrencyProblem): void {
    strategyResults.update(this.pane, { running: false, error: null, currencyProblem: problem })
    this.shown = null
    this.layer.clear()
  }

  /** Settings unchanged since `key` was taken (hiding alone doesn't count). */
  private isCurrent(key: string): boolean {
    return !this.disposed && this.target !== null && runKey(this.target) === key
  }

  /** No run started since this one, and its settings still hold. */
  private isLatest(seq: number, key: string): boolean {
    return seq === this.runSeq && this.isCurrent(key)
  }

  private redraw(): void {
    if (this.shown === null || this.target === null) return
    this.layer.show(this.shown.result, this.shown.bars, !this.target.strategy.hidden)
  }

  /** Stored bars from the deep-backtest start up to `before`, fetched once per series. */
  private olderHistory(target: Target, before: number): Promise<ReadonlyArray<Candle>> {
    const key = `${target.instrument.id}/${target.timeframe}`
    if (this.history?.key !== key) {
      const from = Math.max(0, before - DEEP_HISTORY_SECONDS[target.timeframe])
      const bars = call((client) =>
        client.candles.get({ query: { instrument: target.instrument.id, timeframe: target.timeframe, from, to: before } })
      ).then((response) => response.candles)
      // A failed fetch is retried on the next run.
      bars.catch(() => {
        if (this.history?.bars === bars) this.history = null
      })
      this.history = { key, bars }
    }
    return this.history.bars
  }

  /** Centres the chart on a trade's bar (`time` in epoch seconds). */
  private focus(time: number): void {
    const timeScale = this.chart.timeScale()
    const index = timeScale.timeToIndex(time as never, true)
    const range = timeScale.getVisibleLogicalRange()
    if (index === null || range === null) return
    const half = (range.to - range.from) / 2
    timeScale.setVisibleLogicalRange({ from: index - half, to: index + half })
  }
}

/** What a backtest depends on (everything but `hidden`), including a draft's revision. */
const runKey = ({ instrument, strategy, timeframe }: Target): string =>
  JSON.stringify([
    instrument.id,
    timeframe,
    strategy.id,
    strategy.inputs,
    strategy.properties,
    strategy.deep,
    strategy.from ?? null,
    strategy.to ?? null,
    userDrafts.revision(draftKeyOf(strategy.id))
  ])
